import ImpactPill from "@/components/Impact/ImpactPill";
import RootModal from "@/components/RootModal/RootModal";
import TravelModeCascader from "@/pages/employee/travel_modes/TravelModeCascader";
import styles from "@/pages/employee/travel_modes/TravelModes.module.scss";
import {
  TravelMode,
  TravelModeOption,
  useGetTravelReferenceOptions,
  usePostTravelModeReference,
} from "@/pages/employee/travel_modes/useTravelMode";
import { EditOutlined } from "@ant-design/icons";
import { Button, Col, message, Row } from "antd";
import React, { useEffect, useState } from "react";

interface TravelReferenceSelectorProps {
  selectedItems?: TravelMode[];
  onSuccess?: () => void;
  children?: React.ReactElement;
}

export const getOption = (
  urn: string,
  options?: TravelModeOption[]
): TravelModeOption | undefined => {
  if (!options) {
    return;
  }
  for (const option of options) {
    if (option.value === urn) {
      return option;
    }
    if (option.children) {
      const opt = getOption(urn, option.children);
      if (opt) {
        return opt;
      }
    }
  }
};

function TravelReferenceSelector({
  selectedItems,
  onSuccess,
  children,
}: TravelReferenceSelectorProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<TravelModeOption | undefined>(undefined);
  const selectedKeys = selectedItems?.map((item) => item.id);
  const [urn, setUrn] = useState<string | undefined>();
  const usePost = usePostTravelModeReference();
  const { data, isLoading } = useGetTravelReferenceOptions();

  useEffect(() => {
    if (selectedItems?.length === 1 && selectedItems[0].urn) {
      setUrn(selectedItems[0].urn);
      setSelectedOption(getOption(selectedItems[0].urn, data));
    } else {
      setUrn(undefined);
      setSelectedOption(undefined);
    }
  }, [modalOpen]);

  const onChange = (_: any, selectedOptions: TravelModeOption[] | undefined) => {
    if (selectedOptions) {
      setSelectedOption(selectedOptions[selectedOptions.length - 1]);
    } else {
      setSelectedOption(undefined);
    }
  };

  const onCancel = () => {
    setModalOpen(false);
  };

  const onSubmit = () => {
    if (selectedKeys && selectedOption) {
      usePost.mutateAsync({ travelIds: selectedKeys, urn: selectedOption.value }).then(() => {
        message.success("Impact reference successfully saved.");
        onSuccess?.();
        setModalOpen(false);
      });
    } else {
      console.log(selectedKeys, urn);
      message.error("Please select a impact reference before saving.");
    }
  };

  return (
    <>
      <span onClick={() => setModalOpen(true)}>
        {children || (
          <Button
            onClick={() => setModalOpen(true)}
            type={"primary"}
            icon={<EditOutlined />}
            disabled={selectedItems?.length === 0}
          >
            Edit impact reference
          </Button>
        )}
      </span>

      <RootModal
        open={modalOpen}
        width={750}
        onCancel={() => onCancel()}
        okText={"Save"}
        keyboard={false}
        zIndex={998}
        destroyOnHidden={true}
        title={
          selectedItems?.length === 0 || selectedItems?.length === 1
            ? "Edit impact reference"
            : `Edit impact references for ${selectedItems?.length} travel modes`
        }
        onOk={onSubmit}
        okButtonProps={{ loading: isLoading || usePost.isLoading }}
      >
        <Row>
          <Col span={24}>
            <p>
              We need to connect your travel modes to our impact reference database in order to
              calculate the employee impact.
            </p>
          </Col>
          <Col span={24}>
            <Row
              className={styles["impactContainer"]}
              align={"middle"}
            >
              <Col span={24}>
                <TravelModeCascader
                  urn={urn}
                  onChange={onChange}
                />
              </Col>
              <Col className={styles["impactRow"]}>
                <ImpactPill
                  impact={selectedOption?.impact}
                  type="borderless"
                  loading={isLoading}
                  before={
                    selectedOption && (
                      <span className={styles.impactUnit}>1 {selectedOption.unit}</span>
                    )
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </RootModal>
    </>
  );
}

export default TravelReferenceSelector;
